import { AuthenticationStore, isStoredAccessTokenDueForRenewal, refreshAccessToken } from "@istari-frontend/auth";

import { fromChatMessage } from "~/ai-chat/data-source/adapter/v3/from-chat-message";
import type { ChatMessage } from "~/ai-chat/model/chat-message";
import { resolveRegistryBaseUrl } from "~/shared/config/gateway-urls";

/**
 * A single event from the streaming chat endpoint (`POST /api/v3/ai/chat/stream`).
 *
 * The backend emits Server-Sent Events; this is the decoded, camel-cased shape
 * the UI consumes. Mirrors the backend contract in
 * `fileservice2/v3/ai/ai_dto.py` (StreamEventType + payloads):
 *   - delta      -> incremental assistant text
 *   - tool_call  -> a tool started/finished (name only, no args/results)
 *   - done       -> terminal success, carries mcp_server_url
 *   - error      -> terminal failure (safe message)
 */
export type ChatStreamEvent =
  | { type: "delta"; content: string }
  | { type: "toolCall"; name: string; status: "started" | "finished" }
  // A gated tool call needs explicit human approval before it runs (MCP guard pattern).
  // The turn pauses here; the UI shows approve/deny and POSTs the decision to the
  // approvals endpoint, after which the SAME stream resumes with the result.
  | { type: "approvalRequest"; approvalId: string; tool: string; message: string }
  | { type: "done"; mcpServerUrl: string | null }
  // `requestId` (#20.D) is the backend log correlation id, surfaced on the error
  // bubble so support can pivot a failed session to its log line. Null when the
  // backend didn't attach one (e.g. a pre-stream failure or the synthetic drop).
  | { type: "error"; code: string; message: string; requestId: string | null };

// The registry holds the token it receives here for every tool call of the turn, and a
// turn can run for minutes, so start with at least this much lifetime left.
const minTokenLifetimeSeconds = 10 * 60;

export interface StreamChatMessageParams {
  messages: ChatMessage[];
  systemPrompt?: string | null;
  // Per-user ephemeral LLM key, forwarded as `X-LLM-Api-Key` when set (FE-2).
  llmApiKey?: string | null;
  // The user-chosen model's id (F12). Omit -> server uses the top-priority default.
  modelPk?: string | null;
  // Server-side turn persistence (F14 R3): when set, the stream appends this turn
  // to the conversation itself — the user message + `running` before streaming,
  // then the assistant message + terminal status in its `finally`. Omit -> the
  // server does not persist (v0 behaviour).
  conversationId?: string | null;
  // Resume a turn that paused on an `approvalRequest`: the id the user approved. The server
  // re-executes the gated call and streams the continuation. Requires `conversationId`.
  resumeApprovalId?: string | null;
  signal?: AbortSignal;
  onEvent: (event: ChatStreamEvent) => void;
}

/**
 * Open the streaming chat endpoint and invoke `onEvent` for each decoded event.
 *
 * Resolves when the stream ends (a `done` or `error` event, or the connection
 * closing). Throws before any event if the request fails to open; the thrown
 * value carries `{ response }` so callers can reuse `extractBackendError`
 * (matching the SDK's `ResponseError` shape) to preserve the 404 handling.
 *
 * Uses a hand-rolled `fetch` + `ReadableStream` reader rather than the generated
 * SDK (which buffers/parses the whole body and cannot stream) or
 * `EventSource` (GET-only, cannot send the `Authorization` header).
 */
export async function streamChatMessage({
  messages,
  systemPrompt,
  llmApiKey,
  modelPk,
  conversationId,
  resumeApprovalId,
  signal,
  onEvent,
}: StreamChatMessageParams): Promise<void> {
  if (isStoredAccessTokenDueForRenewal(minTokenLifetimeSeconds)) {
    await refreshAccessToken(minTokenLifetimeSeconds);
  }
  // Read after the refresh: the store snapshots storage on construction.
  const token = AuthenticationStore().getAccessToken() || "";
  const response = await fetch(`${resolveRegistryBaseUrl()}/api/v3/ai/chat/stream`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(llmApiKey ? { "X-LLM-Api-Key": llmApiKey } : {}),
    },
    // The streaming route is not in the OpenAPI spec (and the generated client no longer carries a ChatRequest
    // since the blocking route was removed), so the snake_case body is written here. The backend expects
    // `system_prompt`, not `systemPrompt`; `model_pk` and `conversation_id` are omitted when null so the
    // server picks the default model and persists nothing.
    body: JSON.stringify({
      messages: messages.map(fromChatMessage),
      system_prompt: systemPrompt ?? undefined,
      ...(modelPk ? { model_pk: modelPk } : {}),
      ...(conversationId ? { conversation_id: conversationId } : {}),
      ...(resumeApprovalId ? { resume_approval_id: resumeApprovalId } : {}),
    }),
    signal,
  });

  if (!response.ok || response.body === null) {
    // Shape the error like the SDK's ResponseError so useChatSend's
    // extractBackendError (which duck-types `error.response instanceof Response`)
    // still routes 404 -> "not configured".
    throw Object.assign(new Error(`Chat stream failed with status ${response.status}`), { response });
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // A stream that closes without a terminal event (`done`/`error`) is a dropped
  // connection, not success — without this the turn ends silently and the user
  // sees a question with no reply and no explanation.
  let sawTerminalEvent = false;
  const emit = (event: ChatStreamEvent) => {
    // `approvalRequest` is terminal in the non-blocking model: the server ends the stream on it
    // (the turn is paused), so its arrival is a clean end, not a dropped connection.
    if (event.type === "done" || event.type === "error" || event.type === "approvalRequest") {
      sawTerminalEvent = true;
    }
    onEvent(event);
  };

  try {
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      // Normalize line endings so frames split cleanly on a blank line.
      buffer += decoder.decode(value, { stream: true }).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
      let boundary = buffer.indexOf("\n\n");
      while (boundary !== -1) {
        const frame = buffer.slice(0, boundary);
        buffer = buffer.slice(boundary + 2);
        const event = parseFrame(frame);
        if (event) emit(event);
        boundary = buffer.indexOf("\n\n");
      }
    }
    // Flush any bytes held back by the streaming decoder, then parse a trailing
    // frame that wasn't terminated by a blank line.
    buffer += decoder.decode().replace(/\r\n/g, "\n").replace(/\r/g, "\n");
    const tail = parseFrame(buffer);
    if (tail) emit(tail);
    if (!sawTerminalEvent && !signal?.aborted) {
      onEvent({
        type: "error",
        code: "stream_dropped",
        message: "The connection dropped before the reply finished. Please try again.",
        requestId: null,
      });
    }
  } finally {
    reader.releaseLock();
  }
}

// Parse one SSE frame. Comment lines (`:` — used by the 15s keep-alive ping)
// and blank lines are ignored; multiple `data:` lines concatenate per the spec.
function parseFrame(raw: string): ChatStreamEvent | null {
  let eventName = "message";
  const dataLines: string[] = [];
  for (const line of raw.split("\n")) {
    if (line === "" || line.startsWith(":")) continue;
    if (line.startsWith("event:")) {
      eventName = line.slice("event:".length).trim();
    } else if (line.startsWith("data:")) {
      dataLines.push(line.slice("data:".length).replace(/^ /, ""));
    }
  }
  if (dataLines.length === 0) return null;
  let payload: unknown;
  try {
    payload = JSON.parse(dataLines.join("\n"));
  } catch {
    return null;
  }
  return toStreamEvent(eventName, payload);
}

function toStreamEvent(name: string, data: unknown): ChatStreamEvent | null {
  const obj = (data !== null && typeof data === "object" ? data : {}) as Record<string, unknown>;
  switch (name) {
    case "delta":
      return typeof obj.content === "string" ? { type: "delta", content: obj.content } : null;
    case "tool_call":
      return typeof obj.name === "string" && (obj.status === "started" || obj.status === "finished")
        ? { type: "toolCall", name: obj.name, status: obj.status }
        : null;
    case "approval_request":
      // Require both an id AND a non-empty prompt. A card with no prompt would ask the
      // user to approve a privileged action they can't see — drop it (fail-closed: no
      // card, the server-side turn simply times out) rather than render a blind Approve.
      return typeof obj.approval_id === "string" && typeof obj.message === "string" && obj.message.trim() !== ""
        ? {
            type: "approvalRequest",
            approvalId: obj.approval_id,
            tool: typeof obj.tool === "string" ? obj.tool : "unknown",
            message: obj.message,
          }
        : null;
    case "done":
      return { type: "done", mcpServerUrl: typeof obj.mcp_server_url === "string" ? obj.mcp_server_url : null };
    case "error":
      return {
        type: "error",
        code: typeof obj.code === "string" ? obj.code : "unknown",
        message: typeof obj.message === "string" ? obj.message : "The assistant ran into a problem.",
        requestId: typeof obj.request_id === "string" ? obj.request_id : null,
      };
    default:
      return null;
  }
}
